import { LocationHour, OfficeHourType } from './../../../../shared/models/v2/search';
import { Subject } from 'rxjs';
import { Component, ChangeDetectionStrategy, Input, OnInit } from '@angular/core';
import { InstanceService } from '@commonspirit/ngx-instance';
import { DateTime } from 'luxon';
import { ConfigurationKey } from '../../../../core/models/configuration-key';
import '../../../../core/util/array-prototypes';
import { EmbedSource, getEmbedSource } from 'src/app/core/util/root-element';

@Component({
  selector: 'fal-hours-card',
  templateUrl: './hours-card.component.html',
  styleUrls: ['./hours-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HoursCardComponent implements OnInit {
  _additionalHours: Subject<string[]> = new Subject<string[]>();
  is24HoursOpen = false;

  @Input()
  public get hours(): LocationHour[] {
    return this._hours;
  }
  public set hours(value: LocationHour[]) {
    this._hours = value;
    this.title = this.headerTitle;
    this.is24HoursOpen = !!this._hours[0]?.is24Hours;
  }
  @Input() additionalHours: string[] | null;
  @Input() headerTitle: string;
  public hideMoreHours = true;
  public title: string | undefined | OfficeHourType;
  private _hours: LocationHour[] = [];
  public arrayString: string[] = [];
  public isOpen = false;
  public isClosingSoon = false;
  public todaysHours: LocationHour | undefined = undefined;
  public currentDay!: string;
  public openTime!: DateTime | null;
  public closeTime!: DateTime | null;
  private _today!: DateTime;
  private _ianaTimeZoneName = 'America/Phoenix';
  isChart = false;
  public get today(): DateTime {
    return this._today;
  }
  public set today(value: DateTime) {
    this._today = value;
    this.currentDay = this.today.toLocaleString({ weekday: 'long' });
    this.todaysHours = this.hours.find((x) => x.day === this.currentDay);
    this.setOpenClosed();
  }

  public sorter: { [key: string]: number } = {
    monday: 0,
    tuesday: 1,
    wednesday: 2,
    thursday: 3,
    friday: 4,
    saturday: 5,
    sunday: 6
  };

  constructor(public instanceService: InstanceService) {
    this._ianaTimeZoneName = this.instanceService.getConfiguration(ConfigurationKey.LocationsIanaTimeZoneName)?.value as string;
  }

  ngOnInit(): void {
    this.init();
  }

  init(): void {
    this.title = this.headerTitle;
    const cloned = this.hours.map((x) => Object.assign({}, x));
    ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].forEach((day) => {
      if (!cloned.some((hourItem) => hourItem.day.toLowerCase() === day.toLowerCase())) {
        cloned.push({
          id: 101874,
          day,
          openTime: '',
          closeTime: '',
          is24Hours: null,
          officeHourType: {
            name: 'normal',
            externalCode: 0,
            id: 0
          }
        });
      }
    });
    this.hours = cloned;
    this.today = DateTime.fromObject({}, { zone: this._ianaTimeZoneName });
    this.setOpenClosed();
    this.hours.sort((a, b) => {
      const day1 = !!a.day ? a.day?.toLowerCase() : '';
      const day2 = !!b.day ? b.day?.toLowerCase() : '';
      return this.sorter[day1] - this.sorter[day2];
    });
    this.setDayToTop();
    this.validateMoreHours();
  }

  setDayToTop(): void {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = DateTime.fromObject({}, { zone: this._ianaTimeZoneName });
      const newDate = d.plus({ days: i });
      const dateDay = newDate.toLocaleString({ weekday: 'long' }).toLowerCase();
      if (!this.hours.find((x) => x.day?.toLowerCase() === dateDay)) {
        continue;
      }
      const push = this.hours.find((x) => x.day?.toLowerCase() === dateDay);
      if (!!push) {
        arr.push(push);
      }
    }
    this.hours = arr;
  }

  setOpenClosed(): void {
    if (this.todaysHours?.openTime) {
      const openTimeIsoString = `${this.today.toISO().split('T')[0]}T${this.todaysHours?.openTime}`;
      this.openTime = DateTime.fromISO(openTimeIsoString, { zone: this._ianaTimeZoneName });
    }
    if (this.todaysHours?.closeTime) {
      const closeTimeIsoString = `${this.today.toISO().split('T')[0]}T${this.todaysHours.closeTime}`;
      this.closeTime = DateTime.fromISO(closeTimeIsoString, { zone: this._ianaTimeZoneName });
    }
    if (this.openTime && this.closeTime) {
      this.isOpen = this.today >= this.openTime && this.today < this.closeTime;
      if (this.isOpen) {
        const hoursUntilClosing = this.closeTime.diff(this.today, 'hours').hours;
        this.isClosingSoon = hoursUntilClosing <= 1;
      }
    }
  }

  getIsToday(day: string | undefined): boolean {
    if (!day) {
      return false;
    }
    return day === this.currentDay;
  }

  getClosingString(): string {
    if (!this.todaysHours) {
      return '';
    }
    if (this.todaysHours.is24Hours) {
      return '';
    } else if (this.closeTime) {
      return ` (closes at ${this.closeTime.toLocaleString({ hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })})`;
    } else {
      return '';
    }
  }

  getOpeningString(): string {
    if (!this.todaysHours) {
      return '';
    }
    if (this.isOpen === false && !!this.openTime) {
      return ` (opens ${
        this.today < this.openTime
          ? 'today at ' + this.openTime.toLocaleString({ hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
          : this.getNextOpenDay()
      })`;
    }
    return '';
  }

  getNextOpenDay(): string {
    let newDate: DateTime = this.openTime ?? DateTime.fromObject({}, { zone: this._ianaTimeZoneName });
    for (let i = 1; i < 6; i++) {
      newDate = newDate.plus({ days: 1 });
      const day = newDate.toLocaleString({ weekday: 'long' });
      const locationHours = this.hours.find((x) => x.day === day);
      if ((!!locationHours?.closeTime && !!locationHours.openTime) || locationHours?.is24Hours) {
        const diff = Math.abs(newDate.toMillis() - this.today.toMillis());
        const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
        if (diffDays === 1) {
          return `tomorrow at ${DateTime.fromISO(`${newDate.toISO().split('T')[0]}T${locationHours.openTime}`, {
            zone: this._ianaTimeZoneName
          }).toLocaleString({
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short'
          })}`;
        } else {
          return `${!!locationHours.day ? locationHours.day.toString() : ''} at ${DateTime.fromISO(
            `${newDate.toISO().split('T')[0]}T${locationHours.openTime}`,
            { zone: this._ianaTimeZoneName }
          ).toLocaleString({ hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`.trim();
        }
      } else {
        continue;
      }
    }
    return '';
  }

  validateMoreHours(): void {
    this.hideMoreHours = Boolean(!this.additionalHours?.hasValue());
  }

  showAdditionalHours(additionalHours: string[] | null): void {
    if (additionalHours != null) this._additionalHours.next(additionalHours);
    const embedSource = getEmbedSource();
    if (embedSource === EmbedSource.EPIC_MY_CHART) {
      this.isChart = true;
      const message = {
        fal: true,
        height: document.body.scrollHeight,
        width: document.body.scrollWidth,
        event: 'change-route'
      };
      window?.top?.postMessage(message, '*');
    }
  }
}
